import React, { useEffect } from 'react';
import { XIcon, ArrowUpIcon, CheckIcon } from 'lucide-react';
import { PlanTier, FeatureKey } from '../../types/entitlements';
import { upsellCopy, trackUpsellEvent } from '../../utils/upsellCopy';
import { getRequiredPlan } from '../../hooks/useEntitlements';
interface ContextualPopupProps {
  isOpen: boolean;
  onClose: () => void;
  intent: 'limit' | 'pro_click' | 'milestone';
  feature: FeatureKey;
  onUpgrade: (plan: PlanTier) => void;
  onComparePlans?: () => void;
}
export function ContextualPopup({
  isOpen,
  onClose,
  intent,
  feature,
  onUpgrade,
  onComparePlans
}: ContextualPopupProps) {
  if (!isOpen) return null;
  const requiredPlan = getRequiredPlan(feature);
  const planDetails = upsellCopy.plansDetailed[requiredPlan as keyof typeof upsellCopy.plansDetailed];
  const featureName = upsellCopy.featureNames[feature];
  const roiSnippet = upsellCopy.roiSnippets[feature];
  const handleUpgrade = () => {
    trackUpsellEvent('upsell_click', {
      surface: 'contextual_popup',
      cta: 'upgrade',
      plan: requiredPlan,
      feature
    });
    onUpgrade(requiredPlan);
  };
  const handleComparePlans = () => {
    trackUpsellEvent('upsell_click', {
      surface: 'contextual_popup',
      cta: 'compare_plans',
      feature
    });
    onComparePlans?.();
  };
  useEffect(() => {
    if (isOpen) {
      trackUpsellEvent('upsell_impression', {
        surface: 'contextual_popup',
        feature,
        planSuggested: requiredPlan
      });
    }
  }, [isOpen, feature, requiredPlan]);
  return <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} data-id="element-3509" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3510">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" data-id="element-3511">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-3512">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-3513">
              {intent === 'limit' ? 'Upgrade to Continue' : `Unlock ${featureName}`}
            </h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-3514">
              <XIcon className="w-5 h-5 text-gray-500" data-id="element-3515" />
            </button>
          </div>
          <div className="p-6" data-id="element-3516">
            <p className="text-sm text-gray-600 mb-4" data-id="element-3517">{roiSnippet}</p>
            <div className="mb-6" data-id="element-3518">
              <div className="flex items-center gap-2 mb-3" data-id="element-3519">
                <ArrowUpIcon className="w-5 h-5 text-purple-600" data-id="element-3520" />
                <span className="font-semibold text-gray-900" data-id="element-3521">
                  {planDetails.title}
                </span>
              </div>
              <ul className="space-y-2" data-id="element-3522">
                {planDetails.benefits.slice(0, 3).map((benefit, index) => <li key={index} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3523">
                    <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" data-id="element-3524" />
                    <span data-id="element-3525">{benefit}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex gap-3" data-id="element-3526">
              <button onClick={handleUpgrade} className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all font-medium" data-id="element-3527">
                {upsellCopy.ctas.upgrade}
              </button>
              <button onClick={handleComparePlans} className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-3528">
                {upsellCopy.ctas.comparePlans}
              </button>
            </div>
            <button onClick={onClose} className="w-full mt-3 text-sm text-gray-600 hover:text-gray-900" data-id="element-3529">
              {upsellCopy.ctas.continue}
            </button>
          </div>
        </div>
      </div>
    </>;
}